import { computed } from 'vue';
import { queryOptions, useQueryClient } from '@tanstack/vue-query';
import {
  getCustomerOrganisation,
  getCustomerOrganisationProfile,
  getCustomerOrganisations,
} from '@/api/customerOrganisations';
import { customerOrganisationKeys } from './keys';
import {
  CUSTOMER_ORGANISATION_DETAIL_STALE_TIME_MS,
  CUSTOMER_ORGANISATION_PROFILE_STALE_TIME_MS,
  CUSTOMER_ORGANISATIONS_STALE_TIME_MS,
} from './shared/staleTimes';
import { useCatalogueQuery } from './shared/catalogue';
import { useScopedQuery } from './shared/useScopedQuery';
import type { MaybeId, QueryFlags } from './shared/types';

export function customerOrganisationsListOptions() {
  return queryOptions({
    queryKey: customerOrganisationKeys.list(),
    queryFn: ({ signal }) => getCustomerOrganisations(signal),
    staleTime: CUSTOMER_ORGANISATIONS_STALE_TIME_MS,
  });
}

export function customerOrganisationDetailOptions(
  customerOrganisationId: string
) {
  return queryOptions({
    queryKey: customerOrganisationKeys.detail(customerOrganisationId),
    queryFn: ({ signal }) =>
      getCustomerOrganisation(customerOrganisationId, signal),
    staleTime: CUSTOMER_ORGANISATION_DETAIL_STALE_TIME_MS,
  });
}

export function customerOrganisationProfileOptions(
  customerOrganisationId: string
) {
  return queryOptions({
    queryKey: customerOrganisationKeys.profile(customerOrganisationId),
    queryFn: ({ signal }) =>
      getCustomerOrganisationProfile(customerOrganisationId, signal),
    staleTime: CUSTOMER_ORGANISATION_PROFILE_STALE_TIME_MS,
  });
}

export function useCustomerOrganisationsQuery({
  enabled = true,
}: QueryFlags = {}) {
  const query = useCatalogueQuery(customerOrganisationsListOptions, {
    enabled,
  });

  const customerOrganisations = computed(() => query.data.value ?? []);

  return { customerOrganisations, query };
}

export function useCustomerOrganisationProfileQuery(
  customerOrganisationId: MaybeId,
  flags?: QueryFlags
) {
  const query = useScopedQuery(
    customerOrganisationId,
    customerOrganisationProfileOptions,
    { ...flags, refetchOnWindowFocus: true }
  );

  const customerOrganisationProfile = computed(() => query.data.value);

  return { customerOrganisationProfile, query };
}

export function useCustomerOrganisationDetailQuery(
  customerOrganisationId: MaybeId,
  flags?: QueryFlags
) {
  const query = useScopedQuery(
    customerOrganisationId,
    customerOrganisationDetailOptions,
    {
      ...flags,
      refetchOnWindowFocus: true,
    }
  );

  const customerOrganisation = computed(() => query.data.value);

  return { customerOrganisation, query };
}

export function useInvalidateCustomerOrganisations() {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: customerOrganisationKeys.list(),
    });
}

/** After a write on one customer organisation: its detail and the list it appears in. */
export function useInvalidateCustomerOrganisationDetail() {
  const queryClient = useQueryClient();

  return (customerOrganisationId: string) =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: customerOrganisationKeys.detail(customerOrganisationId),
      }),
      queryClient.invalidateQueries({
        queryKey: customerOrganisationKeys.list(),
      }),
    ]);
}
